import React, { useMemo, useState } from 'react';
import { API, TRACKER } from '~/constants';
import { Combo } from '@fx-ui/fine-design';
import {
    get,
    getApi,
    post,
    hasFunctionContactPhone,
    compact,
    size,
    tracker,
    map,
    SiteRouterKey
} from '~/utils';
import Intl from '@fx-ui/intl';
import { useStores } from '~/hooks';
import { ExtValues, TEMPLATE_FILE_NAME } from '~/interfaces';
import ImportUploadPanel from '../import-upload-panel';
import ImportFailDetail from '../import-fail-detail';
import ImportMemberTip from '../import-member-tip';
import ImportMemberPanel from '../import-member-panel';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import siteRouterManager from '~/utils/site-router';
import { getMemberFieldColumnWidth, trackImport } from '../../utils';

interface IExemptTrialImportPanelProps {
    onImportSuccess: () => void;
}

enum IMPORT_TYPE {
    INVITE, // 成员接受邀请后加入使用
    EXEMPT_TRIAL // 企业直接创建并分配给成员使用
}

interface ICanceledMember {
    user: {
        dept: string;
        email: string;
        loginname: string;
        nickname: string;
        phone: string;
        username: string;
        ext?: ExtValues; // 预设字段
    };
    err: {
        tag: string;
        message: React.ReactNode;
    };
}

/**
 * 通讯录-邀请成员-导入免审（独享版）
 * @param props
 */
const ExemptTrialImportPanel: React.FC<IExemptTrialImportPanelProps> = (props) => {
    const { onImportSuccess } = props;

    const [importType, setImportType] = useState(IMPORT_TYPE.INVITE);
    const [importFailDetailVisible, setImportFailDetailVisible] = useState<boolean>(false);
    const [importInfo, setImportInfo] = useState<{
        token?: string;
        canceled?: ICanceledMember[];
    }>();

    const { corpUserStore, memberFieldStore } = useStores();

    const importTypeItems = useMemo(() => {
        return compact([
            {
                text: Intl.get('member.import.type.invite'),
                value: IMPORT_TYPE.INVITE
            },
            corpUserStore.hasSysAdmin && {
                text: Intl.get('member.import.type.exempt.trial'),
                value: IMPORT_TYPE.EXEMPT_TRIAL
            }
        ]);
    }, []);

    const templateUrl = siteRouterManager.getTemplateFileSource(
        TEMPLATE_FILE_NAME.CONTACTS_EXEMPT_TRIAL_IMPORT_TEMPLATE,
        SiteRouterKey.AdminTemplateFile,
        corpUserStore.tenantId
    );

    const getFailTableColumns = () => {
        const columns = [
            {
                fixed: true,
                minWidth: 136,
                title: Intl.get('name'),
                dataIndex: 'nickname',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return rowData.user?.nickname;
                }
            },
            {
                minWidth: 136,
                title: Intl.get('login.name'),
                dataIndex: 'loginname',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return rowData.user?.loginname;
                }
            },
            {
                minWidth: 136,
                title: Intl.get('serial.number'),
                dataIndex: 'username',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return rowData.user?.username;
                }
            },
            {
                minWidth: 180,
                title: Intl.get('email'),
                dataIndex: 'email',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return rowData.user?.email;
                }
            },
            {
                minWidth: 92,
                title: Intl.get('department'),
                dataIndex: 'dept',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return rowData.user?.dept;
                }
            },
            ...map(memberFieldStore.presetAndCustomFields, (field) => {
                return {
                    title: field.label,
                    dataIndex: field.key,
                    minWidth: getMemberFieldColumnWidth(field),
                    render: (_: unknown, rowData: ICanceledMember) => {
                        return get(rowData, ['user', 'ext', field.key]);
                    }
                };
            }),
            {
                fixed: true,
                minWidth: 180,
                title: Intl.get('trigger.log.failure.reason'),
                dataIndex: 'errorMsg',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return <div className="cell-data-error">{rowData.err?.message}</div>;
                }
            }
        ];

        if (hasFunctionContactPhone()) {
            columns.splice(4, 0, {
                minWidth: 136,
                title: Intl.get('phone'),
                dataIndex: 'phone',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return rowData.user?.phone;
                }
            });
        }

        return columns;
    };

    const handleImport = (fileData: any) => {
        return post(
            getApi(API.corp.excel_process.exempt_trial_import_member, corpUserStore.tenantId),
            {
                fileKey: fileData.file.key
            },
            {
                customHandleHttpCodes: [400]
            }
        ).then((res) => {
            const importInfo = get(res, 'data', {});
            const successCount = importInfo.memberSize;
            handleImportSuccess(successCount);
            setImportInfo(importInfo);
            trackImport(
                [
                    'nickname',
                    'username',
                    'phone',
                    'email',
                    'dept',
                    ...map(memberFieldStore.presetAndCustomFields, 'key')
                ],
                importInfo.members
            );
            return {
                successCount,
                failCount: size(importInfo.canceled)
            };
        });
    };

    const renderExemptTrialImport = () => {
        return (
            <>
                <ImportMemberTip
                    tips={[
                        Intl.getHTML('fxp.base.bg.intra_org.download_template_modify_import.tip', {
                            link: Intl.get('contact.template'),
                            Link: (s) => (
                                <Hyperlink
                                    compact
                                    underline={false}
                                    target="_self"
                                    href={templateUrl}
                                >
                                    {s}
                                </Hyperlink>
                            )
                        }),
                        Intl.get('contact.template.import.tip', { num: 500 }),
                        Intl.get('member.import.tip.exempt.trial')
                    ]}
                />
                <ImportUploadPanel
                    className="upload-panel"
                    uploadPolicyUrl={getApi(
                        API.corp.get_member_excel_token,
                        corpUserStore.tenantId
                    )}
                    onImport={handleImport}
                    onViewFailDetail={() => setImportFailDetailVisible(true)}
                />
                <ImportFailDetail
                    visible={importFailDetailVisible}
                    onClose={() => setImportFailDetailVisible(false)}
                    token={importInfo?.token}
                    exportApi={getApi(
                        API.corp.excel_process.exempt_trial_error_export,
                        corpUserStore.tenantId
                    )}
                    columns={getFailTableColumns()}
                    tableData={importInfo?.canceled || []}
                />
            </>
        );
    };

    const handleImportSuccess = (successCount = 0) => {
        tracker(TRACKER.MEMBER_JOIN_METHOD_CHOOSE, {
            p1: importType === IMPORT_TYPE.INVITE ? 'bymember' : 'bycorp',
            p2: successCount
        });
        onImportSuccess();
    };

    return (
        <div className="fx-exempt-trial-import-panel">
            <div className="import-type">
                <div className="label">{Intl.get('import.type')}</div>
                <Combo
                    value={importType}
                    items={importTypeItems}
                    hasClear={false}
                    hasSearch={false}
                    style={{ flex: 1 }}
                    onValueChange={(v: IMPORT_TYPE) => setImportType(v)}
                />
            </div>
            {importType === IMPORT_TYPE.INVITE ? (
                <ImportMemberPanel onImportSuccess={handleImportSuccess} />
            ) : (
                renderExemptTrialImport()
            )}
        </div>
    );
};

export default ExemptTrialImportPanel;
